Object.assign(window.NomaCareProductFinder.prototype, {
  availableTargetSet() {
    return new Set(this.catalogue.flatMap((product) => product.targets));
  },

  availableCategories() {
    const targets = this.availableTargetSet();
    const hasAny = (...values) => values.some((value) => targets.has(value));

    return NOMA_CARE_FINDER.categories.filter((option) => {
      if (option.value === 'sexual') {
        return hasAny('chlamydia', 'gonorrhoea', 'hiv', 'syphilis', 'hsv', 'hpv');
      }
      if (option.value === 'pregnancy') return targets.has('pregnancy');
      if (option.value === 'bloodborne') return hasAny('hepb', 'hepc', 'hiv', 'syphilis');
      if (option.value === 'prostate') return targets.has('psa');
      if (option.value === 'bundles') return this.catalogue.some((product) => product.format === 'bundle');
      if (option.value === 'unsure') return this.catalogue.length > 1;
      return false;
    });
  },

  currentStep() {
    return this.route[this.stepIndex];
  },

  configureRoute(category) {
    const routes = {
      sexual: ['category', 'targets', 'format', 'context'],
      pregnancy: ['category', 'context'],
      bloodborne: ['category', 'targets', 'format', 'context'],
      prostate: ['category', 'context'],
      bundles: ['category', 'coverage', 'context'],
      unsure: ['category'],
    };

    this.route = routes[category] || ['category'];
    delete this.answers.targets;
    delete this.answers.coverage;
    delete this.answers.format;
    delete this.answers.context;

    const availableFormats = [...new Set(this.catalogue.map((product) => product.format))];
    if ((category === 'sexual' || category === 'bloodborne') && availableFormats.length === 1) {
      this.route = this.route.filter((step) => step !== 'format');
      this.answers.format = availableFormats[0];
    }

    if (category === 'pregnancy') {
      this.answers.targets = ['pregnancy'];
      this.answers.format = 'single';
    }

    if (category === 'prostate') {
      this.answers.targets = ['psa'];
      this.answers.format = 'single';
    }

    if (category === 'bundles') {
      this.answers.format = 'bundle';
    }
  },

  questionDefinition(step) {
    if (step === 'category') {
      return {
        title: 'What type of product do you want to compare?',
        copy: 'Start with a broad health area. The next step will show only relevant choices.',
        type: 'radio',
        columns: 2,
        options: this.availableCategories(),
      };
    }

    if (step === 'targets') {
      const availableTargets = this.availableTargetSet();
      const options = (NOMA_CARE_FINDER.targetsByCategory[this.answers.category] || [])
        .filter(([value]) => availableTargets.has(value))
        .map(([value, icon, label, copy]) => ({ value, icon, label, copy }));

      return {
        title: 'Which screening target or targets do you want to compare?',
        copy: 'Select one or more. Products are matched only by their stated catalogue coverage.',
        type: 'checkbox',
        columns: this.answers.category === 'bloodborne' ? 2 : 3,
        options,
      };
    }

    if (step === 'format') {
      return {
        title: 'Which product format would you prefer?',
        copy: 'This changes the ordering of results. It does not determine medical suitability.',
        type: 'radio',
        columns: 3,
        options: [
          {
            value: 'single',
            label: 'Focused single-target products',
            copy: 'Prioritise products aimed at one stated target.',
          },
          {
            value: 'bundle',
            label: 'Broader multi-target products',
            copy: 'Prioritise multi-target products that include the selected target or targets.',
          },
          {
            value: 'compare',
            label: 'Show both formats',
            copy: 'Compare focused and multi-target products together.',
          },
        ],
      };
    }

    if (step === 'coverage') {
      const options = [
        {
          value: 'common-sti',
          label: NOMA_CARE_FINDER.coverageGroups['common-sti'].label,
          copy: 'Compare the 2-in-1 gonorrhoea and chlamydia format and overlapping products.',
        },
        {
          value: 'bloodborne',
          label: NOMA_CARE_FINDER.coverageGroups.bloodborne.label,
          copy: 'Compare the blood-borne four-target format and overlapping products.',
        },
        {
          value: 'hiv-syphilis-herpes',
          label: NOMA_CARE_FINDER.coverageGroups['hiv-syphilis-herpes'].label,
          copy: 'Compare packs centred on these screening targets.',
        },
        {
          value: 'five-target',
          label: NOMA_CARE_FINDER.coverageGroups['five-target'].label,
          copy: 'Compare five-target STI screening packs.',
        },
        {
          value: 'compare-bundles',
          label: 'Compare all published multi-target products',
          copy: 'Review the available combination products.',
        },
      ].filter(
        (option) =>
          option.value === 'compare-bundles' ||
          this.catalogue.some((product) => product.groups.includes(option.value)),
      );

      return {
        title: 'Which multi-target coverage do you want to compare?',
        copy: 'Choose the combination closest to the screening targets you are looking for.',
        type: 'radio',
        columns: 2,
        options,
      };
    }

    const options =
      this.answers.category === 'pregnancy' || this.answers.category === 'prostate'
        ? [
            {
              value: 'routine',
              label: 'Routine product comparison',
              copy: 'I am comparing catalogue products and their stated information.',
            },
            {
              value: 'concern',
              label: 'Symptoms or a medical concern',
              copy: 'I may need professional assessment rather than product matching alone.',
            },
          ]
        : [
            {
              value: 'routine',
              label: 'Routine screening or product comparison',
              copy: 'I am comparing products and their stated screening targets.',
            },
            {
              value: 'recent',
              label: 'A recent possible exposure',
              copy: 'I need to consider timing and possible detection windows.',
            },
            {
              value: 'concern',
              label: 'Symptoms or a medical concern',
              copy: 'I may need professional assessment rather than product matching alone.',
            },
          ];

    return {
      title: 'Which statement best describes why you are looking now?',
      copy: 'This does not change product coverage. It determines whether a timing or care reminder appears before the results.',
      type: 'radio',
      columns: options.length,
      options,
    };
  },

  renderQuestion() {
    const step = this.currentStep();
    const question = this.questionDefinition(step);

    if (!question.options.length) {
      this.renderCatalogueUnavailable();
      return;
    }

    this.progressRegion.hidden = false;
    this.actions.hidden = false;
    this.questionTitle.textContent = question.title;
    this.questionCopy.textContent = question.copy;
    this.optionContainer.replaceChildren();
    this.optionContainer.dataset.columns = String(question.columns || 2);

    question.options.forEach((option) => {
      this.optionContainer.append(this.createOption(step, question.type, option));
    });

    const current = this.stepIndex + 1;
    const total = this.route.length;
    const progressPercent = Math.round((current / total) * 100);

    this.progress.style.setProperty('--nc-progress', `${progressPercent}%`);
    this.progress.setAttribute('aria-valuemax', String(total));
    this.progress.setAttribute('aria-valuenow', String(current));
    this.progressLabel.textContent = `Step ${current}`;
    this.progressCount.textContent = `${current} of ${total}`;
    this.backButton.hidden = this.stepIndex === 0;
    this.nextButton.textContent =
      this.stepIndex === total - 1
        ? this.answers.category === 'unsure'
          ? 'Browse categories'
          : 'See catalogue matches'
        : 'Continue';

    this.error.hidden = true;

    requestAnimationFrame(() => {
      this.questionTitle.focus({ preventScroll: true });
    });
  },

  renderCatalogueUnavailable() {
    this.progressRegion.hidden = true;
    this.actions.hidden = true;
    this.error.hidden = true;
    this.questionTitle.textContent = 'Product Finder is temporarily unavailable';
    this.questionCopy.textContent =
      'No published products are currently configured for this step. You can still browse the full catalogue.';
    this.optionContainer.dataset.columns = '1';
    this.optionContainer.replaceChildren();

    const empty = document.createElement('div');
    empty.className = 'nc-product-finder__empty';

    const title = document.createElement('strong');
    title.textContent = 'No Finder products are available right now.';

    const copy = document.createElement('p');
    copy.textContent =
      'Browse all products or contact NomaCare if you need help finding current catalogue information.';

    const link = document.createElement('a');
    link.className = 'nc-product-finder__button nc-product-finder__button--secondary';
    link.href = '/collections/all';
    link.textContent = 'Browse all products';

    empty.append(title, copy, link);
    this.optionContainer.append(empty);

    requestAnimationFrame(() => {
      this.questionTitle.focus({ preventScroll: true });
    });
  },

  createOption(step, type, option) {
    const label = document.createElement('label');
    label.className = 'nc-product-finder__option';

    const input = document.createElement('input');
    input.className = 'nc-product-finder__option-input';
    input.type = type;
    input.name = type === 'checkbox' ? `${step}[]` : step;
    input.value = option.value;

    if (type === 'checkbox') {
      input.checked = Array.isArray(this.answers[step]) && this.answers[step].includes(option.value);
    } else {
      input.checked = this.answers[step] === option.value;
    }

    input.addEventListener('change', () => {
      this.error.hidden = true;
    });

    const card = document.createElement('span');
    card.className = 'nc-product-finder__option-card';
    if (!option.icon) card.dataset.textOnly = 'true';

    if (option.icon) {
      const icon = document.createElement('span');
      icon.className = 'nc-product-finder__option-icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = option.icon;
      card.append(icon);
    }

    const text = document.createElement('span');
    const title = document.createElement('span');
    title.className = 'nc-product-finder__option-title';
    title.textContent = option.label;
    const copy = document.createElement('span');
    copy.className = 'nc-product-finder__option-copy';
    copy.textContent = option.copy;
    text.append(title, copy);

    const check = document.createElement('span');
    check.className = 'nc-product-finder__option-check';
    check.setAttribute('aria-hidden', 'true');
    check.textContent = '✓';

    card.append(text, check);
    label.append(input, card);

    return label;
  },

  readAnswer(step) {
    const inputs = [...this.optionContainer.querySelectorAll('input')];

    if (step === 'targets') {
      return inputs.filter((input) => input.checked).map((input) => input.value);
    }

    return inputs.find((input) => input.checked)?.value || '';
  },

  goForward() {
    const step = this.currentStep();
    const answer = this.readAnswer(step);
    const isEmpty = Array.isArray(answer) ? answer.length === 0 : !answer;

    if (isEmpty) {
      this.error.textContent =
        step === 'targets' ? 'Select at least one screening target to continue.' : 'Choose one option to continue.';
      this.error.hidden = false;
      this.optionContainer.querySelector('input')?.focus();
      return;
    }

    const previousCategory = this.answers.category;
    this.answers[step] = answer;

    if (step === 'category' && previousCategory !== answer) {
      this.answers.category = answer;
      this.configureRoute(answer);
    }

    if (this.stepIndex >= this.route.length - 1) {
      this.showResults();
      return;
    }

    this.stepIndex += 1;
    this.renderQuestion();
  },

  goBack() {
    if (this.stepIndex === 0) return;
    this.stepIndex -= 1;
    this.renderQuestion();
  }
});
